import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { AuthLayout, Field } from "./Auth";

/**
 * Access-code sign-in.
 *
 * The founder receives an email with a pill CTA that lands them here at
 * `/access-code-login?code=CPG-FOUNDER-XXXX`. The code is pre-filled so
 * they only type their email and hit Login. On success we consume the
 * `{token, user}` payload the backend returns (mirrors `/auth/login`),
 * hand it to AuthContext.setUser + setStatus so the Protected routes
 * see the founder as authenticated on the very first click.
 *
 * Layout uses the shared `AuthLayout` so this screen matches the main
 * `/` login and `/signup` — gradient promo panel on the left, form
 * card on the right — instead of the older centered pill card that
 * felt disconnected from the rest of the auth flow.
 */
export default function AccessCodeLogin() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser, setStatus } = useAuth();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const c = params.get("code");
    if (c) setCode(c.trim().toUpperCase());
    const em = params.get("email");
    if (em) setEmail(em.trim());
  }, [loc.search]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/access-code-login", {
        email: email.trim(), code: code.trim(),
      });
      if (data?.token) {
        try { window.localStorage.setItem("token", data.token); } catch { /* noop */ }
      }
      if (data?.user) {
        if (setUser) setUser(data.user);
        if (setStatus) setStatus("authenticated");
      }
      toast.success("Signed in");
      nav("/welcome");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Welcome to CPG Institute">
      <div className="mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-shell-border bg-white text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
        <KeyRound className="w-3 h-3" /> Access-code sign-in
      </div>
      <p className="-mt-2 mb-6 text-sm text-muted-foreground">
        Enter your email to activate the access your invite included.
      </p>
      <form onSubmit={submit} className="space-y-4" data-testid="access-code-login-form">
        <Field
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          testid="access-code-login-email"
          placeholder="you@yourbrand.com"
          autoFocus
          autoComplete="email"
        />
        <Field
          label="Access code"
          value={code}
          onChange={(v) => setCode(v.toUpperCase())}
          testid="access-code-login-code"
          placeholder="CPG-…"
          spellCheck={false}
          inputClassName="font-mono tracking-[0.08em] uppercase"
        />
        <button
          type="submit"
          disabled={busy || !email.trim() || !code.trim()}
          data-testid="access-code-login-submit"
          className="btn-primary w-full justify-center"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">
        Don't have an access code?{" "}
        <Link to="/" className="text-primary font-semibold" data-testid="access-code-login-back">
          Sign in with password
        </Link>
      </p>
    </AuthLayout>
  );
}
